import { useEffect, useState } from "react";
import { listDamageReports, listTools, createDamageReport, reviewDamageReport, exportDamageReportsCSV } from "@/lib/firestore";
import { authedFileUrl } from "@/lib/files";
import { DMG } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Warning, CheckCircle, XCircle, DownloadSimple } from "@phosphor-icons/react";
import MultiImageUploader from "@/components/MultiImageUploader";

const SEVERITIES = ["minor", "moderate", "severe"];

export default function DamageReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canReport = user?.permissions?.can_report_damage !== false;
  const [reports, setReports] = useState([]);
  const [tools, setTools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tool_id: "", description: "", severity: "moderate", photo_urls: [] });
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    const [r, t] = await Promise.all([listDamageReports(user), listTools()]);
    setReports(r);
    setTools(t);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createDamageReport(form, user);
      toast.success("Damage report submitted");
      setShowForm(false);
      setForm({ tool_id: "", description: "", severity: "moderate", photo_urls: [] });
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const review = async (id, action) => {
    try {
      await reviewDamageReport(id, action, "", user);
      toast.success(`Report ${action}d`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const runExportCSV = async () => {
    try {
      await exportDamageReportsCSV(user);
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// INCIDENTS</div>
          <h1 className="font-display font-black text-4xl">DAMAGE REPORTS</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">
            {isAdmin ? "Review and approve incoming damage reports." : "Report broken or worn tools."}
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button
              onClick={runExportCSV}
              data-testid="export-damage-csv"
              className="brutal-btn border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
            >
              <DownloadSimple size={16} weight="bold" /> Export CSV
            </button>
          )}
          {canReport && (
            <button
              data-testid={DMG.createBtn}
              onClick={() => setShowForm(true)}
              className="brutal-btn bg-[#FF4F00] text-black px-4 py-2.5 hover:bg-[#FF6B2B] flex items-center gap-2 transition-colors duration-150"
            >
              <Plus size={16} weight="bold" /> Report Damage
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// NEW DAMAGE REPORT</div>
                <h2 className="font-display font-black text-2xl">Report Damage</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <select data-testid={DMG.toolSelect} required value={form.tool_id} onChange={(e) => setForm({ ...form, tool_id: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                <option value="">Select tool...</option>
                {tools.map((t) => <option key={t.id} value={t.id}>{t.name} · #{t.tool_code}</option>)}
              </select>
              <select data-testid={DMG.severitySelect} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea data-testid={DMG.descInput} rows={4} required placeholder="What happened?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-2 block">Photos (up to 5)</label>
                <MultiImageUploader
                  value={form.photo_urls}
                  onChange={(paths) => setForm({ ...form, photo_urls: paths })}
                  max={5}
                  testId="damage-photos"
                />
              </div>
              <button type="submit" data-testid={DMG.submitBtn} className="brutal-btn w-full bg-[#FF4F00] text-black px-4 py-3 hover:bg-[#FF6B2B] transition-colors duration-150">
                Submit Report →
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {reports.length === 0 && (
          <div className="brutal-card p-10 text-center text-[#52525B] font-mono">// no damage reports //</div>
        )}
        {reports.map((r) => (
          <div key={r.id} className="brutal-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Warning size={16} weight="bold" className={`${r.severity === "severe" ? "text-[#D32F2F]" : r.severity === "moderate" ? "text-[#FFD700]" : "text-[#A1A1AA]"}`} />
                <div>
                  <div className="font-display font-bold text-lg">{r.tool_name}</div>
                  <div className="font-mono text-xs text-[#52525B]">by {r.reported_by_name} · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  r.severity === "severe" ? "border-[#D32F2F] text-[#D32F2F]" :
                  r.severity === "moderate" ? "border-[#FFD700] text-[#FFD700]" :
                  "border-[#A1A1AA] text-[#A1A1AA]"
                }`}>{r.severity}</span>
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  r.status === "pending" ? "border-[#FFD700] text-[#FFD700]" :
                  r.status === "approved" ? "border-[#00E676] text-[#00E676]" :
                  "border-[#D32F2F] text-[#D32F2F]"
                }`}>{r.status}</span>
              </div>
            </div>
            <div className="text-sm text-[#A1A1AA] mb-3">{r.description}</div>
            {(r.photo_urls || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {(r.photo_urls || []).map((p, i) => (
                  <img
                    key={p}
                    src={authedFileUrl(p)}
                    alt={`damage ${i + 1}`}
                    onClick={() => setLightbox(p)}
                    data-testid={`damage-photo-${r.id}-${i}`}
                    className="w-20 h-20 object-cover border border-[#27272A] cursor-pointer hover:border-[#FF4F00] transition-colors duration-150"
                  />
                ))}
              </div>
            )}
            {isAdmin && r.status === "pending" && (
              <div className="flex gap-2 border-t border-[#1a1a1e] pt-3">
                <button
                  data-testid={DMG.approveBtn(r.id)}
                  onClick={() => review(r.id, "approve")}
                  className="brutal-btn border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-4 py-2 flex items-center gap-1 text-xs transition-colors duration-150"
                >
                  <CheckCircle size={14} weight="bold" /> Approve
                </button>
                <button
                  data-testid={DMG.rejectBtn(r.id)}
                  onClick={() => review(r.id, "reject")}
                  className="brutal-btn border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-4 py-2 flex items-center gap-1 text-xs transition-colors duration-150"
                >
                  <XCircle size={14} weight="bold" /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="damage-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 border border-[#FF4F00] text-[#FF4F00] flex items-center justify-center hover:bg-[#FF4F00] hover:text-black transition-colors duration-150"
          >
            <X size={20} weight="bold" />
          </button>
          <img src={authedFileUrl(lightbox)} alt="damage full" className="max-h-[90vh] max-w-full border border-[#27272A]" />
        </div>
      )}
    </div>
  );
}
